type NoopAnalyticsMethod = (...args: unknown[]) => void

/** A stand-in for `window.analytics` that accepts calls and drops them. */
export type NoopAnalytics = Record<string, NoopAnalyticsMethod>

/**
 * The analytics.js snippet surface (SNIPPET_VERSION 4.15.2), mirrored from
 * ./segment.ts so the stub is a drop-in for call sites that don't guard their
 * `analytics.track`/`identify` calls.
 */
export const SEGMENT_SNIPPET_METHODS: readonly string[] = [
  'trackSubmit',
  'trackClick',
  'trackLink',
  'trackForm',
  'pageview',
  'identify',
  'reset',
  'group',
  'track',
  'ready',
  'alias',
  'debug',
  'page',
  'once',
  'off',
  'on',
  'addSourceMiddleware',
  'addIntegrationMiddleware',
  'setAnonymousId',
  'addDestinationMiddleware',
]

/**
 * Builds the `window.analytics` replacement installed when analytics is
 * skipped for a page view.
 *
 * Deliberately a plain object rather than the snippet's array: the real
 * snippet queues every call until analytics.js loads, and a queue that never
 * drains would grow for as long as the page lives. Nothing is recorded, so
 * `ready` callbacks never fire and no call returns a chainable analytics
 * instance — both are no-ops for the call sites we have (see ./README.md).
 *
 * It is also truthy on purpose: app-shell's `trackUser` injects its own
 * Segment snippet when `window.analytics` is missing.
 */
export function createNoopAnalytics(): NoopAnalytics {
  const analytics: NoopAnalytics = {}

  for (const method of SEGMENT_SNIPPET_METHODS) {
    analytics[method] = (): void => undefined
  }

  return analytics
}
